import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User } from '../api/types';

interface AuthSliceState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  needsOTP: boolean;
  otpEmail: string | null;
  googleDrive: {
    status: 'idle' | 'refreshing' | 'connected' | 'required';
    connectedEmail: string | null;
  };
}

const initialState: AuthSliceState = {
  isAuthenticated: false,
  user: null,
  isLoading: false,  // Don't start with loading
  error: null,
  needsOTP: false,
  otpEmail: null,
  googleDrive: {
    status: 'idle',
    connectedEmail: null,
  },
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    loginSuccess: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.isLoading = false;
      state.error = null;
      state.needsOTP = false;
      state.otpEmail = null;
    },

    loginOTPRequired: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.needsOTP = true;
      state.otpEmail = action.payload;
    },

    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.needsOTP = false;
      state.otpEmail = null;
      state.googleDrive = { status: 'idle', connectedEmail: null };
    },

    setGoogleDriveConnection: (
      state,
      action: PayloadAction<{
        status: AuthSliceState['googleDrive']['status'];
        connectedEmail?: string | null;
      }>,
    ) => {
      state.googleDrive.status = action.payload.status;
      if (action.payload.connectedEmail !== undefined) {
        state.googleDrive.connectedEmail = action.payload.connectedEmail;
      }
    },

    setOTPState: (state, action: PayloadAction<{ needsOTP: boolean; otpEmail: string | null }>) => {
      state.needsOTP = action.payload.needsOTP;
      state.otpEmail = action.payload.otpEmail;
    },

    clearOTPState: (state) => {
      state.needsOTP = false;
      state.otpEmail = null;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  clearError,
  loginSuccess,
  loginOTPRequired,
  logout,
  setGoogleDriveConnection,
  setOTPState,
  clearOTPState,
} = authSlice.actions;

export default authSlice.reducer;
